import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, PLANNER_STEPS, CONTACT, waLink } from "@/data/site";
import { SectionHeading } from "@/components/pp/Shared";
import { submitLead, track } from "@/lib/api";

const initial = {
  name: "", company: "", phone: "", email: "", services: [], budget: "",
  details: "", preferred_contact: "WhatsApp", consent: false,
};

export default function Contact() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) => setData((d) => ({ ...d, services: d.services.includes(s) ? d.services.filter((x) => x !== s) : [...d.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !/^\d{7,15}$/.test(data.phone.replace(/\s|-/g, ""))) { toast.error("Please enter your name and a valid phone number."); return; }
    if (!data.consent) { toast.error("Please accept the consent to proceed."); return; }
    setLoading(true);
    track("form_submit", { source: "contact_form" });
    try {
      await submitLead({ ...data, source: "contact_form" });
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp.");
    } finally { setLoading(false); }
  };

  const info = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: MessageCircle, label: "WhatsApp", value: "+91 " + CONTACT.whatsapp.slice(2), href: waLink() },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: "Office", value: CONTACT.address },
    { icon: Clock, label: "Hours", value: CONTACT.hours },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-32" data-testid="contact">
      <div className="pp-container grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeading overline="Contact" title="Let's Build Something Bold" testid="contact-heading" />
          <div className="space-y-5">
            {info.map((it) => (
              <div key={it.label} className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-xl pp-glass grid place-items-center text-[#f59e0b] shrink-0"><it.icon size={18} /></span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{it.label}</p>
                  {it.href ? <a href={it.href} target={it.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-slate-200 hover:text-[#f59e0b] transition-colors">{it.value}</a> : <p className="text-slate-200">{it.value}</p>}
                </div>
              </div>
            ))}
            <p className="text-[11px] uppercase tracking-widest text-slate-600 pt-2">Email, address & hours are editable placeholders.</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="pp-glass rounded-2xl p-10 h-full flex flex-col items-center justify-center text-center" data-testid="contact-success">
              <CheckCircle2 className="text-[#25D366] mb-4" size={56} />
              <h3 className="font-display uppercase text-3xl text-white">Message Received</h3>
              <p className="mt-3 text-slate-400 max-w-sm">Thanks for reaching out. Our team will get back to you soon. Prefer instant? Ping us on WhatsApp.</p>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-black font-semibold uppercase tracking-widest text-xs px-6 py-3.5"><MessageCircle size={16} /> Chat Now</a>
            </div>
          ) : (
            <form onSubmit={submit} className="pp-glass rounded-2xl p-7 md:p-9 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <input data-testid="contact-name" placeholder="Name *" value={data.name} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                <input data-testid="contact-company" placeholder="Company" value={data.company} onChange={(e) => set("company", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                <input data-testid="contact-phone" placeholder="Phone number *" value={data.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
                <input data-testid="contact-email" placeholder="Email" value={data.email} onChange={(e) => set("email", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none" />
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Required services</p>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((s) => {
                    const active = data.services.includes(s.title);
                    return (
                      <button type="button" key={s.id} onClick={() => toggleService(s.title)} data-testid={`contact-service-${s.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active ? "bg-[#f59e0b] text-black" : "bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10"}`}>
                        {s.title}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <select data-testid="contact-budget" value={data.budget} onChange={(e) => set("budget", e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none">
                  <option value="" className="bg-[#0a0a0e]">Monthly budget</option>
                  {PLANNER_STEPS.budgets.map((b) => <option key={b} value={b} className="bg-[#0a0a0e]">{b}</option>)}
                </select>
                <select data-testid="contact-preferred" value={data.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#f59e0b] focus:outline-none">
                  {["WhatsApp", "Phone Call", "Email"].map((p) => <option key={p} value={p} className="bg-[#0a0a0e]">{p}</option>)}
                </select>
              </div>

              <textarea data-testid="contact-details" placeholder="Project details" value={data.details} onChange={(e) => set("details", e.target.value)} rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#f59e0b] focus:outline-none resize-none" />

              <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-400" data-testid="contact-consent-label">
                <input type="checkbox" data-testid="contact-consent" checked={data.consent} onChange={(e) => set("consent", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#f59e0b]" />
                I agree to be contacted by PixelPapad regarding my enquiry.
              </label>

              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#f59e0b] text-black font-semibold uppercase tracking-widest text-sm py-4 hover:bg-white transition-colors disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send Enquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
